import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useAskUiStoreApi } from '@/components/libraryChat/LibraryChatContext';
import { putAskTurn, type AskConversationSnapshot, type AskTurn } from '@/services/ask/api';
import { toAskError, type AskError } from '@/services/ask/errors';
import {
  askKeys,
  getAskConversationV2QueryOptions,
  refreshAskHistory,
} from '@/services/ask/queryOptions';
import { trackLibraryChatTurnFailed, trackLibraryChatTurnSubmitted } from '@/util/analyticsEvents';

export type SubmitAskTurnVariables = {
  conversationId: string;
  /** Generated once per user action by the caller, and reused on every resend of it. */
  turnId: string;
  text: string;
  parentTurnId: string | null;
  acknowledgedIdentifierWarning?: boolean;
  isRetry?: boolean;
  /**
   * Whether the composer's terms checkbox was accepted for this send. Reaches
   * only Segment, not the request body: the API has no field for it today, so
   * this is a client-side record of the gate having been satisfied, not a
   * server-side compliance record.
   */
  termsAccepted?: boolean;
};

export type SubmitAskTurnResult = {
  /** Differs from the requested id only when a stale parent was repaired. */
  turnId: string;
};

/** How many times a submit that got no response is resent, under the same turn id. */
const NETWORK_RETRIES = 2;

function optimisticTurn(variables: SubmitAskTurnVariables, turnIndex: number): AskTurn {
  const now = new Date().toISOString();

  return {
    id: variables.turnId,
    parentTurnId: variables.parentTurnId,
    turnIndex,
    text: variables.text,
    status: 'queued',
    stage: null,
    errorCode: null,
    context: null,
    results: [],
    createdAt: now,
    completedAt: null,
  };
}

function seedSnapshot(conversationId: string, turn: AskTurn): AskConversationSnapshot {
  return {
    id: conversationId,
    title: null,
    version: 0,
    latestTurnId: null,
    primaryCancerType: null,
    createdAt: turn.createdAt,
    updatedAt: turn.createdAt,
    turns: [turn],
  };
}

/**
 * Appends a queued turn under its real id, keeping the snapshot's `version`, so
 * the next read (higher version) replaces it and no hint for it looks stale.
 */
function addOptimisticTurn(queryClient: QueryClient, variables: SubmitAskTurnVariables): void {
  queryClient.setQueryData<AskConversationSnapshot>(
    askKeys.snapshot(variables.conversationId),
    (snapshot) => {
      if (!snapshot) {
        return seedSnapshot(variables.conversationId, optimisticTurn(variables, 0));
      }

      if (snapshot.turns.some((turn) => turn.id === variables.turnId)) {
        return snapshot;
      }

      const parent = snapshot.turns.find((turn) => turn.id === variables.parentTurnId);

      const turn = optimisticTurn(variables, parent ? parent.turnIndex + 1 : 0);

      return { ...snapshot, turns: [...snapshot.turns, turn] };
    }
  );
}

/**
 * Takes an optimistic turn back out. A snapshot that holds nothing but that
 * turn was seeded for a new conversation the server never created, so it is
 * removed outright rather than left as an empty conversation.
 */
function removeOptimisticTurn(queryClient: QueryClient, conversationId: string, turnId: string) {
  const key = askKeys.snapshot(conversationId);

  const snapshot = queryClient.getQueryData<AskConversationSnapshot>(key);

  if (!snapshot) {
    return;
  }

  if (snapshot.version === 0 && snapshot.turns.every((turn) => turn.id === turnId)) {
    queryClient.removeQueries({ queryKey: key, exact: true });

    return;
  }

  queryClient.setQueryData<AskConversationSnapshot>(key, {
    ...snapshot,
    turns: snapshot.turns.filter((turn) => turn.id !== turnId),
  });
}

/**
 * Asks a question in a conversation.
 *
 * The turn id is the caller's, fixed for one user action. A request that got
 * no response is resent automatically under that same id, and the server
 * answers a repeated id with the turn it already has, so a flaky network can
 * never create a second turn for one question.
 *
 * `CONVERSATION_ADVANCED` means another device completed a turn since this one
 * last read the conversation. It is repaired here, once: send the same text
 * under a new id with the latest turn the refusal names as parent (re-reading
 * the snapshot only if the refusal did not say). A second refusal goes back to
 * the caller with the draft intact.
 *
 * Every other refusal takes the optimistic turn back out and is returned to
 * the caller, which owns the copy and what to offer next.
 */
export function useSubmitAskTurn() {
  const queryClient = useQueryClient();

  const store = useAskUiStoreApi();

  /**
   * The repaired send for each submit, keyed by its variables object, which
   * TanStack passes unchanged to every retry. A retry after a repaired send
   * lost its response must resend that same repaired id, not repair again
   * under a new one: the first may have landed, and a second id would then be
   * refused as a turn in progress, the reader's own.
   */
  const repairs = useRef(new WeakMap<SubmitAskTurnVariables, SubmitAskTurnVariables>());

  return useMutation<
    SubmitAskTurnResult,
    AskError,
    SubmitAskTurnVariables,
    { submittedAt: number }
  >({
    mutationFn: async (variables) => {
      const send = async (attempt: SubmitAskTurnVariables) => {
        try {
          await putAskTurn({
            conversationId: attempt.conversationId,
            turnId: attempt.turnId,
            body: {
              text: attempt.text,
              parentTurnId: attempt.parentTurnId,
              ...(attempt.acknowledgedIdentifierWarning && {
                acknowledgedIdentifierWarning: true,
              }),
            },
          });
        } catch (caught) {
          throw toAskError(caught);
        }
      };

      const sendRepaired = async (repaired: SubmitAskTurnVariables) => {
        addOptimisticTurn(queryClient, repaired);

        try {
          await send(repaired);
        } catch (error) {
          removeOptimisticTurn(queryClient, variables.conversationId, repaired.turnId);

          throw error;
        }

        return { turnId: repaired.turnId };
      };

      const earlierRepair = repairs.current.get(variables);

      if (earlierRepair) {
        return sendRepaired(earlierRepair);
      }

      try {
        await send(variables);

        return { turnId: variables.turnId };
      } catch (caught) {
        const error = toAskError(caught);

        if (error.kind !== 'CONVERSATION_ADVANCED') {
          throw error;
        }

        const latestTurnId =
          error.latestTurnId !== undefined
            ? error.latestTurnId
            : (
                await queryClient.fetchQuery(
                  getAskConversationV2QueryOptions(variables.conversationId)
                )
              ).latestTurnId;

        const repaired = { ...variables, turnId: uuidv4(), parentTurnId: latestTurnId };

        repairs.current.set(variables, repaired);

        return sendRepaired(repaired);
      }
    },

    retry: (failureCount, error) => error.kind === 'network' && failureCount < NETWORK_RETRIES,

    /*
     * Always attempt, even when the device reports itself offline. The default
     * pauses an offline mutation until connectivity returns, which leaves the
     * composer empty and "sending" indefinitely; failing lets the reader see the
     * connection message with the draft back in the box.
     */
    networkMode: 'always',

    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: askKeys.snapshot(variables.conversationId) });

      addOptimisticTurn(queryClient, variables);

      return { submittedAt: Date.now() };
    },

    onSuccess: (result, variables, context) => {
      if (result.turnId !== variables.turnId) {
        removeOptimisticTurn(queryClient, variables.conversationId, variables.turnId);
      }

      store.getState().markSubmitted(result.turnId, context?.submittedAt ?? Date.now());

      trackLibraryChatTurnSubmitted({
        conversation_id: variables.conversationId,
        turn_id: result.turnId,
        turn_index: turnIndexOf(queryClient, variables.conversationId, result.turnId),
        text_length: variables.text.length,
        is_retry: variables.isRetry ?? false,
        is_follow_up: variables.parentTurnId != null,
        // Only meaningful for a conversation's first turn; a follow-up never asks for it.
        ...(variables.parentTurnId == null && {
          terms_accepted: variables.termsAccepted ?? false,
        }),
      });
    },

    onError: (error, variables) => {
      removeOptimisticTurn(queryClient, variables.conversationId, variables.turnId);

      trackLibraryChatTurnFailed({
        conversation_id: variables.conversationId,
        turn_id: variables.turnId,
        error_code: error.kind,
        ...(error.status != null && { status: error.status }),
      });
    },

    onSettled: (_result, _error, variables) => {
      queryClient
        .invalidateQueries({ queryKey: askKeys.snapshot(variables.conversationId) })
        .catch(() => {});

      refreshAskHistory(queryClient);
    },
  });
}

function turnIndexOf(queryClient: QueryClient, conversationId: string, turnId: string): number {
  return (
    queryClient
      .getQueryData<AskConversationSnapshot>(askKeys.snapshot(conversationId))
      ?.turns.find((turn) => turn.id === turnId)?.turnIndex ?? 0
  );
}
